import React, { useRef, useState } from "react";
/** Assets */
import { Bars3Icon, FolderIcon } from "@heroicons/react/24/outline";
import {
  BellIcon,
  ChatIcon,
  DocumentListIcon,
  FolderListIcon,
  HomeIcon,
  LogoutIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  SearchIcon,
} from "assets/icons/HeroIcons";
import {
  ArrowUpRightIcon,
  BinocularsIcon,
  CameraIcon,
  // FolderListIcon,
} from "assets/icons/HeroIcons";
import ISNLogo from "assets/logo/ISNLogo";
import CommunitiesIcon from "assets/icons/HeroIcons/CommunitiesIcon";
import { Toasts } from "view/components/Toasts";
import apiLibrary from "services/api";
import { MAXIMUM_FILE_SIZE } from "constants/FileSize";
import { getCurrentBranding } from "utils/customBranding";
import { brandLogos } from "hooks/useCustomBranding/constants";
import usePermissions from "hooks/usePermissions";

import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import circleImageDefault from "assets/images/Placeholders/avatar-circle4.png";
import { setLoadingProgress } from "store/loadingBar";
import { getRandomProgress } from "utils/getRandomProgress";
import { useDispatch } from "react-redux";

interface ImageData {
  title: string;
  value: any | null;
  description: string;
  imageKey: string;
  fileId: number | null;
  filePath: string | null;
  appName: string;
  getUniversalSettings: () => void;
  setSettings: any;
}

export const ObserverDashboardWeb: React.FC<ImageData> = ({
  title,
  value,
  description,
  imageKey,
  fileId,
  filePath,
  appName,
  getUniversalSettings,
  setSettings,
}) => {
  const dispatch = useDispatch();
  const { universalSettings, universalSettingsImages } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);

  const currentBrand = getCurrentBranding();
  const CURRENT_BRAND_LOGO = brandLogos[currentBrand];

  const fileInputRef = useRef<HTMLInputElement>(null);


  /**
   * Handles the upload of a user's profile image.
   * Accepts only JPEG/JPG/PNG with EXACT dimensions of 1920×600.
   */
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.target;
    const file = input.files?.[0];

    const resetInput = () => {
      input.value = "";
    };

    const getImageDimensions = (
      f: File
    ): Promise<{ width: number; height: number }> =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(f);
        const img = new Image();
        img.onload = () => {
          const dims = { width: img.width, height: img.height };
          URL.revokeObjectURL(url);
          resolve(dims);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Invalid image file."));
        };
        img.src = url;
      });

    try {
      if (!file) return;

      dispatch(setLoadingProgress(getRandomProgress()));

      const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validImageTypes.includes(file.type)) {
        Toasts.error("Please select a valid image file (jpeg/jpg/png).");
        resetInput();
        return;
      }

      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 25) {
        Toasts.error("File size must be less than 25 MB!");
        resetInput();
        return;
      }

      const { width, height } = await getImageDimensions(file);
      if (width !== 1920 || height !== 600) {
        Toasts.error("Please ensure the image is exactly 1920×600 pixels.");
        resetInput();
        return;
      }

      dispatch(setLoadingProgress(getRandomProgress()));

      setIsLoading(true);

      const uploadResponse = await apiLibrary.file.fileUpload(file);

      const { message } = await apiLibrary.systemSettings.updateUniversalImages(
        imageKey,
        appName,
        uploadResponse.data.id
      );

      Toasts.success(message);
      await getUniversalSettings();
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ??
        error?.message ??
        "Something went wrong.";
      Toasts.error(errorMsg);
      resetInput();
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };

  /**
   * Handles the click event to trigger the file input element.
   */
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetToDefault = async () => {
    if (fileId === null) return;
    try {
      setIsLoading(true);
      dispatch(setLoadingProgress(getRandomProgress()));

      const { data, message } =
        await apiLibrary.systemSettings.updateUniversalImages(
          imageKey,
          appName,
          null
        );
      Toasts.success(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      getUniversalSettings();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };

  return (
    <div className="w-full h-[342px] col-span-2">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Dashboard Web
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices, use an image that's 1920 x 600
            px.
          </p>
          <br />
          <button
            type="button"
            onClick={resetToDefault}
            disabled={isLoading || fileId === null}
            className="text-primary text-xs font-semibold mt-2 cursor-pointer disabled:cursor-not-allowed"
          >
            Reset to default
          </button>
        </div>

        <div className="flex">
          {/* Side bar */}
          <div className="h-[265px] w-[60px] bg-bgWhite dark:bg-secondaryLight">
            {/** Header logos */}
            <div className="flex justify-between py-1 items-center px-1">
              <div>
                <Bars3Icon className="w-2 h-2 dark:stroke-caption" />
              </div>
              <div className="mr-5">
                <ISNLogo width={8} height={8} />
              </div>
              <div>
                <div className="flex flex-col items-center">
                  <BellIcon className="w-2 h-2 relative" fill="#2C3236" />
                  <div className="absolute h-[4px] w-[4px] rounded-full bg-bgWhite mt-0 ml-[4px] flex items-center justify-center">
                    <div className="h-[3px] w-[3px] rounded-full bg-primaryMid"></div>
                  </div>
                </div>
              </div>
            </div>

            {/** Name and Image Section */}
            <div className="flex items-center gap-1 px-1 my-1">
              <img
                src={circleImageDefault}
                alt=""
                className="w-2 h-2 rounded-full"
              />
              <p className="text-secondaryMid text-[4px] font-semibold p-0 m-0">
                Aaron Lestenkof
              </p>
            </div>

            {/** Search Section */}
            <div className="pt-[2px] px-1">
              <div className="w-full py-[2px] border-lineDark border flex items-center px-1 gap-1 rounded-sm">
                <span>
                  <SearchIcon className=" w-2 h-2 " />
                </span>
                <span className="text-textLightExtra text-[4px]">Search</span>
              </div>
            </div>

            {/* observation button */}
            <div className="pt-1 px-1">
              <div className="w-full bg-primary flex py-[3px] rounded-md items-center justify-center gap-1">
                <PlusIcon className="w-1 h-1" fill="#FFFFFF" />
                <span className="text-[3.7px] text-textWhite font-semibold ">
                  New Observation
                </span>
              </div>
            </div>

            {/* sidebar links */}
            <div className="pt-[6px] px-1 flex flex-col gap-[2px]">
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <HomeIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Home
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <BinocularsIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Observations
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <FolderListIcon
                    className="w-[6px] h-[6px]"
                    height={7}
                    width={8}
                    fill="#2C3236"
                  />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Programs
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px] px-[1px]">
                <span>
                  <DocumentListIcon
                    className=""
                    height={6}
                    width={7}
                    fill="#2C3236"
                  />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Forms
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <CommunitiesIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Communities
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <ChatIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Messages
                </span>
              </div>
              <div className="border-t border-lineMid mt-[3px]"></div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px] mt-[3px]">
                <span>
                  <QuestionMarkCircleIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Help and support
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <LogoutIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Sign out
                </span>
              </div>
            </div>
          </div>

          {/* Image section */}
          <div className="relative w-[389px] bg-bgBluish dark:bg-bgMain">
            <div
              className="absolute inset-0 bg-cover bg-center h-[155px] z-10"
              style={{
                backgroundImage: `url(${filePath ? filePath : defaultBackGroundImage})`,
              }}
            />

            <div className="px-1 py-1 flex justify-between items-center text-white text-sm relative z-10">
              <span className="text-[7.52px] text-textWhite relative top-[8px] left-[10px] font-medium">
                Welcome, Amanda
              </span>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={isLoading}
              />

              <button
                onClick={handleClick}
                disabled={isLoading}
                className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight absolute right-[-10px] top-[-12px] z-20"
              >
                <CameraIcon className="w-5 h-5" />
              </button>

              <div className="z-10 relative right-[10px] top-[4px]">
                <button className="bg-primary flex items-center justify-center p-[3px] rounded-lg gap-1">
                  {" "}
                  <span>
                    <PlusIcon className="w-2.5 h-2.5" fill="#FFFFFF" />
                  </span>{" "}
                  <span className="text-[5.83px] text-textWhite font-bold">
                    Add Observation
                  </span>{" "}
                </button>
              </div>
            </div>
            <div className=" px-2 w-full relative z-20 top-5">
              <div className="flex justify-evenly w-full">
                <div className="bg-[#ebf2f64a] rounded-[4px] p-[2px] h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    Number Of Observations
                  </p>
                  <p className="text-textWhite text-sm absolute top-[5px] px-[3px] font-semibold">
                    310
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
                <div className="bg-[#ebf2f64a] rounded-[4px] p-[2px] h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    In Review
                  </p>
                  <p className="text-textWhite text-sm absolute top-[5px] px-[3px] font-semibold">
                    25
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
                <div className="bg-[#ebf2f64a] rounded-[4px] p-[2px] h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    Draft
                  </p>
                  <p className="text-textWhite text-sm absolute top-[5px] px-[3px] font-semibold">
                    310
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
              </div>
            </div>
            <div className=" px-2 w-full relative z-20 top-7">
              <div className="flex justify-evenly w-full">
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
              </div>
            </div>
            <div className="px-3 w-full relative top-9 h-[34.5px] overflow-hidden">
              <div className="">
                <ul className="bg-bgWhite dark:bg-secondaryLight flex flex-col gap-2 border border-borderLightGray dark:border-secondaryMid rounded-[4px] pt-3 pr-3">
                  <li className="border-b border-y-borderLightGray dark:border-secondaryMidLight"></li>
                  <li className="border-b border-y-borderLightGray dark:border-secondaryMidLight"></li>
                  <li className="border-b border-y-borderLightGray dark:border-secondaryMidLight"></li>
                  <li className=""></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
